import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { communicationsAPI, phoneSystemAPI, smsAPI, leadSourcesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { MobileFABContainer, MobileFABMenu, MobileFABMenuItem } from '../components/MobileFAB';
import {
  Phone, MessageSquare, Mic, Plus, Trash2, Edit2, Save,
  PhoneIncoming, Clock, Users, Voicemail, PhoneOff, Settings,
  ArrowRight, Check, Loader2, Shield, X, ChevronDown, ChevronUp,
  BarChart3, Target, Tag, Music, Upload, Play, Pause, GitBranch, Eye,
  ShieldCheck,
} from 'lucide-react';
import UsageDashboardTab from '../components/UsageDashboardTab';
import { IVRFlowBuilder } from '../components/IVRFlowBuilder';
import { FlowPreviewModal, FlowPreviewPanel } from '../components/FlowPreview';
import { FlowSimulatorModal } from '../components/FlowSimulator';
import TextingSetupTab from '../components/TextingSetupTab';


// ═══════════════════════════════════════════════════════════
// PHONE NUMBERS TAB
// ═══════════════════════════════════════════════════════════

function PhoneNumbersTab() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const [loadingImpact, setLoadingImpact] = useState(null);
  const [fabOpen, setFabOpen] = useState(false);
  // Call tracking state
  const [trackingTarget, setTrackingTarget] = useState(null); // number being edited
  const [leadSources, setLeadSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState('');
  const [trackingNotes, setTrackingNotes] = useState('');
  const [newSourceName, setNewSourceName] = useState('');
  const [savingTracking, setSavingTracking] = useState(false);
  const [showNewSource, setShowNewSource] = useState(false);
  // CNAM state
  const [cnamTarget, setCnamTarget] = useState(null);
  const [cnamName, setCnamName] = useState('');
  const [cnamDefault, setCnamDefault] = useState('');
  const [savingCnam, setSavingCnam] = useState(false);

  const load = () => {
    setLoading(true);
    communicationsAPI.lineSummary().then(r => {
      setSummary(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const loadSources = async () => {
    try {
      const res = await leadSourcesAPI.list();
      setLeadSources(res.data || []);
    } catch { /* tenant may not have sources yet */ }
  };

  const openTrackingEditor = (number) => {
    setTrackingTarget(number);
    setCnamTarget(null); // close CNAM editor if open
    setSelectedSource(number.lead_source_id || '');
    setTrackingNotes(number.tracking_notes || '');
    setShowNewSource(false);
    setNewSourceName('');
    loadSources();
  };

  const handleSaveTracking = async () => {
    if (!trackingTarget) return;
    setSavingTracking(true);
    try {
      let sourceId = selectedSource || null;

      // Create new source if needed
      if (showNewSource && newSourceName.trim()) {
        const res = await leadSourcesAPI.create({ name: newSourceName.trim() });
        sourceId = res.data.id;
      }

      await communicationsAPI.updateTracking(trackingTarget.id, {
        lead_source_id: sourceId,
        tracking_notes: trackingNotes || null,
        designation: sourceId ? 'tracking' : (trackingTarget.is_primary ? 'primary' : 'secondary'),
      });
      toast.success(sourceId ? 'Source assigned' : 'Source cleared');
      setTrackingTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update tracking');
    } finally {
      setSavingTracking(false);
    }
  };

  const openCnamEditor = async (number) => {
    setCnamTarget(number);
    setCnamName(number.cnam_display_name || '');
    setTrackingTarget(null); // close tracking editor if open
    try {
      const res = await communicationsAPI.cnamDefaults();
      setCnamDefault(res.data.default_name || '');
    } catch { setCnamDefault(''); }
  };

  const handleSaveCnam = async () => {
    if (!cnamTarget) return;
    setSavingCnam(true);
    try {
      await communicationsAPI.updateCnam(cnamTarget.id, {
        display_name: cnamName.trim() || null,
      });
      toast.success(cnamName.trim() ? 'Caller name submitted' : 'Caller name cleared');
      setCnamTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update caller name');
    } finally {
      setSavingCnam(false);
    }
  };

  const handleReleasePreview = async (numberId) => {
    setLoadingImpact(numberId);
    try {
      const res = await communicationsAPI.releaseNumber(numberId, false);
      setReleaseTarget({ numberId, impact: res.data.impact });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to preview release');
    } finally {
      setLoadingImpact(null);
    }
  };

  const handleConfirmRelease = async () => {
    if (!releaseTarget) return;
    setReleasing(true);
    try {
      await communicationsAPI.releaseNumber(releaseTarget.numberId, true);
      toast.success(`Released ${releaseTarget.impact.phone_number}`);
      setReleaseTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Release failed');
    } finally {
      setReleasing(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!summary) return <p className="text-sm text-muted-foreground p-4">Unable to load phone numbers.</p>;

  const imp = releaseTarget?.impact;

  return (
    <div className="space-y-6" data-testid="phone-numbers-tab">
      {/* Line usage summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard label="Included Lines" value={summary.included_lines} />
        <SummaryCard label="Assigned" value={summary.assigned_lines} />
        <SummaryCard label="Remaining" value={summary.remaining_lines} accent="green" />
        <SummaryCard label="Extra Lines" value={summary.extra_lines} accent={summary.extra_lines > 0 ? 'amber' : undefined} sub={summary.extra_lines > 0 ? `$${(summary.extra_line_cost_cents / 100).toFixed(0)}/mo each` : null} />
      </div>

      {/* Release confirmation dialog */}
      {imp && (
        <Card className="border-red-200 bg-red-50/50" data-testid="release-confirmation">
          <CardContent className="py-5 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
                <Shield className="w-4 h-4 text-red-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-900">Release {imp.phone_number}?</p>
                <p className="text-xs text-red-800 mt-1">This will deactivate the number. Please review the impact below.</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 ml-0 sm:ml-11">
              {/* Routing impact */}
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Routing</p>
                {imp.will_remove_from_flows ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5">
                    <p className="text-xs text-amber-800">Will be removed from {imp.affected_call_flows.length} call flow(s):</p>
                    <ul className="mt-1 space-y-0.5">
                      {imp.affected_call_flows.map(f => (
                        <li key={f.id} className="text-xs text-amber-700 flex items-center gap-1">
                          <PhoneIncoming className="w-3 h-3" /> {f.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Not assigned to any call flows.</p>
                )}
                {imp.is_primary && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5">
                    <p className="text-xs text-amber-800">This is your primary number. It will be unset as primary.</p>
                  </div>
                )}
              </div>

              {/* Billing & data impact */}
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Billing & Data</p>
                {imp.extra_line_savings_cents > 0 ? (
                  <div className="bg-green-50 border border-green-200 rounded-md p-2.5">
                    <p className="text-xs text-green-800">
                      Extra lines: {imp.current_extra_lines} → {imp.after_extra_lines}.
                      Saves ${(imp.extra_line_savings_cents / 100).toFixed(2)}/mo.
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Active lines: {imp.current_active_count} → {imp.after_active_count}. No billing change.
                  </p>
                )}
                <div className="bg-muted/50 rounded-md p-2.5">
                  <p className="text-xs text-muted-foreground">
                    Call logs, SMS history, and conversation records are preserved.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2 ml-0 sm:ml-11">
              <Button size="sm" variant="outline" onClick={() => setReleaseTarget(null)} data-testid="cancel-release-btn">
                Cancel
              </Button>
              <Button size="sm" variant="destructive" onClick={handleConfirmRelease} disabled={releasing} data-testid="confirm-release-btn">
                {releasing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
                Release Number
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Numbers list */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="text-base">Your Phone Numbers</CardTitle>
            <div className="hidden sm:flex gap-2">
              <Button size="sm" variant="outline" onClick={() => window.location.href = '/phone-setup'} data-testid="port-numbers-btn">
                Port Numbers
              </Button>
              <Button size="sm" onClick={() => window.location.href = '/phone-setup'} data-testid="get-numbers-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Get New Numbers
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {summary.numbers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-testid="no-numbers-msg">
              <Phone className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No phone numbers yet. Get new numbers or port existing ones.</p>
            </div>
          ) : (
            <div className="divide-y">
              {summary.numbers.map(n => (
                <div key={n.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-3" data-testid={`number-row-${n.id}`}>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Phone className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{n.phone_number}</p>
                      <p className="text-xs text-muted-foreground truncate">{n.friendly_name || n.designation}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap pl-6 sm:pl-0">
                    <div className="hidden sm:flex items-center gap-1">
                      {n.voice_enabled && <Badge variant="outline" className="text-[10px]">Voice</Badge>}
                      {n.sms_enabled && <Badge variant="outline" className="text-[10px]">SMS</Badge>}
                    </div>
                    {n.has_call_flow && <Badge className="bg-green-100 text-green-700 text-[10px]">Routed</Badge>}
                    {n.is_primary && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Primary</Badge>}
                    {n.lead_source_name ? (
                      <Badge
                        className="bg-violet-100 text-violet-700 text-[10px] cursor-pointer hover:bg-violet-200 transition-colors"
                        onClick={() => openTrackingEditor(n)}
                        data-testid={`source-badge-${n.id}`}
                      >
                        <Target className="w-2.5 h-2.5 mr-0.5" />
                        {n.lead_source_name}
                      </Badge>
                    ) : (
                      <button
                        onClick={() => openTrackingEditor(n)}
                        className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground hover:text-foreground border border-dashed border-border rounded-md px-1.5 py-0.5 transition-colors"
                        data-testid={`set-source-btn-${n.id}`}
                      >
                        <Tag className="w-2.5 h-2.5" /> Set Source
                      </button>
                    )}
                    {/* CNAM status */}
                    {n.cnam_status === 'pending' ? (
                      <Badge className="bg-amber-100 text-amber-700 text-[10px] cursor-pointer hover:bg-amber-200" onClick={() => openCnamEditor(n)} data-testid={`cnam-badge-${n.id}`}>
                        CNAM Pending
                      </Badge>
                    ) : n.cnam_status === 'active' ? (
                      <Badge className="bg-emerald-100 text-emerald-700 text-[10px] cursor-pointer hover:bg-emerald-200" onClick={() => openCnamEditor(n)} data-testid={`cnam-badge-${n.id}`}>
                        CNAM Active
                      </Badge>
                    ) : n.cnam_status === 'failed' ? (
                      <Badge className="bg-red-100 text-red-700 text-[10px] cursor-pointer hover:bg-red-200" onClick={() => openCnamEditor(n)} data-testid={`cnam-badge-${n.id}`}>
                        CNAM Failed
                      </Badge>
                    ) : n.cnam_status === 'unsupported' ? (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground cursor-pointer" onClick={() => openCnamEditor(n)} data-testid={`cnam-badge-${n.id}`}>
                        CNAM N/A
                      </Badge>
                    ) : (
                      <button
                        onClick={() => openCnamEditor(n)}
                        className="hidden sm:inline-flex items-center gap-0.5 text-[10px] text-muted-foreground hover:text-foreground border border-dashed border-border rounded-md px-1.5 py-0.5 transition-colors"
                        data-testid={`set-cnam-btn-${n.id}`}
                      >
                        Caller ID
                      </button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                      onClick={() => handleReleasePreview(n.id)}
                      disabled={loadingImpact === n.id}
                      data-testid={`release-btn-${n.id}`}
                    >
                      {loadingImpact === n.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Call Tracking Source Editor */}
      {trackingTarget && (
        <Card className="border-violet-200 bg-violet-50/30" data-testid="tracking-editor">
          <CardContent className="py-4 sm:py-5 space-y-3 sm:space-y-4">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                  <Target className="w-4 h-4 text-violet-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">Assign Source</p>
                  <p className="text-xs text-muted-foreground truncate">{trackingTarget.phone_number}</p>
                </div>
              </div>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setTrackingTarget(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-2.5">
              {!showNewSource ? (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Lead Source</label>
                  <div className="flex gap-2">
                    <select
                      value={selectedSource}
                      onChange={e => setSelectedSource(e.target.value)}
                      className="flex-1 h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                      data-testid="source-select"
                    >
                      <option value="">None (clear source)</option>
                      {leadSources.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      onClick={() => setShowNewSource(true)}
                      data-testid="new-source-btn"
                    >
                      <Plus className="w-3.5 h-3.5 sm:mr-1" />
                      <span className="hidden sm:inline">New</span>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">New Source Name</label>
                  <div className="flex gap-2">
                    <Input
                      value={newSourceName}
                      onChange={e => setNewSourceName(e.target.value)}
                      placeholder="e.g. Google Ads, Yard Sign"
                      className="flex-1"
                      data-testid="new-source-input"
                    />
                    <Button size="sm" variant="ghost" onClick={() => { setShowNewSource(false); setNewSourceName(''); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Notes (optional)</label>
                <Input
                  value={trackingNotes}
                  onChange={e => setTrackingNotes(e.target.value)}
                  placeholder="Campaign name, placement, etc."
                  data-testid="tracking-notes-input"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => setTrackingTarget(null)}>Cancel</Button>
              <Button
                size="sm"
                onClick={handleSaveTracking}
                disabled={savingTracking || (showNewSource && !newSourceName.trim())}
                data-testid="save-tracking-btn"
              >
                {savingTracking ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                {selectedSource || (showNewSource && newSourceName.trim()) ? 'Save Source' : 'Clear Source'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* CNAM Editor */}
      {cnamTarget && (
        <Card className="border-amber-200 bg-amber-50/30" data-testid="cnam-editor">
          <CardContent className="py-4 sm:py-5 space-y-3 sm:space-y-4">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4 text-amber-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">Caller ID Name (CNAM)</p>
                  <p className="text-xs text-muted-foreground truncate">{cnamTarget.phone_number}</p>
                </div>
              </div>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setCnamTarget(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Status display */}
            {cnamTarget.cnam_status && cnamTarget.cnam_status !== 'not_configured' && (
              <div className={`rounded-md px-3 py-2 text-xs ${
                cnamTarget.cnam_status === 'pending' ? 'bg-amber-50 border border-amber-200 text-amber-800' :
                cnamTarget.cnam_status === 'active' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' :
                cnamTarget.cnam_status === 'failed' ? 'bg-red-50 border border-red-200 text-red-800' :
                'bg-muted border text-muted-foreground'
              }`} data-testid="cnam-status-message">
                {cnamTarget.cnam_error || `Status: ${cnamTarget.cnam_status}`}
              </div>
            )}

            <div className="space-y-2">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Business Display Name</label>
                <div className="flex gap-2">
                  <Input
                    value={cnamName}
                    onChange={e => setCnamName(e.target.value.slice(0, 15))}
                    placeholder="e.g. ACME PLUMBING"
                    maxLength={15}
                    className="flex-1 uppercase"
                    data-testid="cnam-name-input"
                  />
                  {cnamDefault && !cnamName && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0 text-xs"
                      onClick={() => setCnamName(cnamDefault.toUpperCase())}
                      data-testid="cnam-use-default-btn"
                    >
                      Use Company
                    </Button>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground tabular-nums">{cnamName.length}/15 characters</p>
              </div>

              {/* Explanation */}
              <div className="bg-muted/50 rounded-md px-3 py-2" data-testid="cnam-info-note">
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Setting a caller name registers your preferred business identity for outbound calls.
                  Full CNAM propagation depends on Twilio Trust Hub approval and carrier support,
                  which may take 48-72 hours. Not all carriers or devices display caller names.
                  <span className="font-medium"> Complete Trust Hub verification below to enable CNAM.</span>
                </p>
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => setCnamTarget(null)}>Cancel</Button>
              <Button
                size="sm"
                onClick={handleSaveCnam}
                disabled={savingCnam || (cnamName.trim().length > 0 && cnamName.trim().length < 2)}
                data-testid="save-cnam-btn"
              >
                {savingCnam ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                {cnamName.trim() ? 'Save Caller Name' : 'Clear Caller Name'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mobile FAB for Phone Numbers */}
      <MobileFABContainer data-testid="numbers-fab-container">
        <MobileFABMenu open={fabOpen} onClose={() => setFabOpen(false)}>
          <MobileFABMenuItem icon={Phone} onClick={() => { setFabOpen(false); window.location.href = '/phone-setup'; }}>
            Get New Numbers
          </MobileFABMenuItem>
          <MobileFABMenuItem icon={ArrowRight} onClick={() => { setFabOpen(false); window.location.href = '/phone-setup'; }}>
            Port Numbers
          </MobileFABMenuItem>
        </MobileFABMenu>
        <button
          onClick={() => setFabOpen(!fabOpen)}
          data-testid="numbers-mobile-fab"
          className="w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center transition-transform duration-150 active:scale-90"
        >
          <Plus className={`w-6 h-6 transition-transform ${fabOpen ? 'rotate-45' : ''}`} strokeWidth={2.5} />
        </button>
      </MobileFABContainer>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// TEXT MESSAGING TAB
// ═══════════════════════════════════════════════════════════

const DEFAULT_TEMPLATES = [
  { category: 'job_scheduled', name: 'Job Scheduled', body: 'Hi {{customer_first_name}}, your appointment with {{company_name}} is confirmed for {{job_date}} at {{job_time}}. Questions? Call us anytime.' },
  { category: 'on_my_way', name: 'On My Way', body: 'Hi {{customer_first_name}}, {{technician_first_name}} from {{company_name}} is on the way to your location. See you soon!' },
  { category: 'job_finished', name: 'Job Finished', body: 'Hi {{customer_first_name}}, {{technician_first_name}} has completed your service. Invoice #{{invoice_number}} has been sent. Thank you for choosing {{company_name}}!' },
];

const MERGE_TAGS = [
  { tag: '{{customer_first_name}}', label: 'Customer First Name' },
  { tag: '{{company_name}}', label: 'Company Name' },
  { tag: '{{technician_first_name}}', label: 'Tech First Name' },
  { tag: '{{technician_last_name}}', label: 'Tech Last Name' },
  { tag: '{{invoice_number}}', label: 'Invoice Number' },
  { tag: '{{job_date}}', label: 'Job Date' },
  { tag: '{{job_time}}', label: 'Job Time' },
];

function TextMessagingTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState('');

  const load = useCallback(() => {
    communicationsAPI.smsTemplates().then(r => {
      setTemplates(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const ensureDefaults = async () => {
    for (const dt of DEFAULT_TEMPLATES) {
      const exists = templates.find(t => t.category === dt.category);
      if (!exists) {
        await smsAPI.templates.create({ name: dt.name, template_text: dt.body, category: dt.category });
      }
    }
    toast.success('Default templates created');
    load();
  };

  const handleSave = async (tpl) => {
    try {
      await smsAPI.templates.update(tpl.id, { template_text: editBody });
      toast.success('Template saved');
      setEditingId(null);
      load();
    } catch {
      toast.error('Failed to save');
    }
  };

  const handleToggle = async (tpl) => {
    // Toggle is a no-op for now — SmsTemplate model doesn't have is_active field
    toast.info('Template toggling coming soon');
  };

  const previewText = (body) => {
    return body
      .replace('{{customer_first_name}}', 'John')
      .replace('{{company_name}}', 'Summit Plumbing')
      .replace('{{technician_first_name}}', 'Mike')
      .replace('{{technician_last_name}}', 'Sullivan')
      .replace('{{invoice_number}}', 'INV-1042')
      .replace('{{job_date}}', 'March 28')
      .replace('{{job_time}}', '10:00 AM');
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const categorized = DEFAULT_TEMPLATES.map(dt => {
    const existing = templates.find(t => t.category === dt.category);
    return { ...dt, ...(existing || {}), exists: !!existing };
  });

  return (
    <div className="space-y-4" data-testid="text-messaging-tab">
      {categorized.every(c => !c.exists) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
          <p className="text-sm text-blue-800">Create default SMS templates for common job stages.</p>
          <Button size="sm" onClick={ensureDefaults} data-testid="create-defaults-btn">Create Defaults</Button>
        </div>
      )}

      {categorized.map(tpl => (
        <Card key={tpl.category} data-testid={`template-${tpl.category}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">{tpl.name}</CardTitle>
              {tpl.exists && (
                <Badge variant="outline" className="text-[10px]">{tpl.category.replace(/_/g, ' ')}</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {tpl.exists ? (
              editingId === tpl.id ? (
                <div className="space-y-3">
                  <Textarea
                    value={editBody}
                    onChange={e => setEditBody(e.target.value)}
                    rows={3}
                    className="text-sm"
                    data-testid={`edit-body-${tpl.category}`}
                  />
                  <div className="flex flex-wrap gap-1">
                    {MERGE_TAGS.map(mt => (
                      <button key={mt.tag} onClick={() => setEditBody(prev => prev + mt.tag)} className="text-[10px] px-2 py-0.5 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground">
                        {mt.label}
                      </button>
                    ))}
                  </div>
                  <div className="bg-muted/50 rounded p-2">
                    <p className="text-[10px] text-muted-foreground mb-1">Preview:</p>
                    <p className="text-xs">{previewText(editBody)}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleSave(tpl)} data-testid={`save-${tpl.category}`}><Save className="w-3 h-3 mr-1" />Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">{tpl.body}</p>
                  <div className="bg-muted/50 rounded p-2 mb-2">
                    <p className="text-[10px] text-muted-foreground mb-1">Preview:</p>
                    <p className="text-xs">{previewText(tpl.body)}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setEditingId(tpl.id); setEditBody(tpl.body); }} data-testid={`edit-${tpl.category}`}>
                    <Edit2 className="w-3 h-3 mr-1" />Edit
                  </Button>
                </div>
              )
            ) : (
              <div className="py-2">
                <p className="text-xs text-muted-foreground">Not created yet. Use "Create Defaults" above to generate this template.</p>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// VOICE TAB
// ═══════════════════════════════════════════════════════════

// CallFlowBuilder is now provided by IVRFlowBuilder (see /components/IVRFlowBuilder.jsx)


function VoiceTab() {
  const [flows, setFlows] = useState([]);
  const [ringGroups, setRingGroups] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [voiceSettings, setVoiceSettings] = useState(null);
  const [audioFiles, setAudioFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFlow, setEditingFlow] = useState(null); // null | 'new' | flow object
  const [newRgName, setNewRgName] = useState('');
  const [showRgForm, setShowRgForm] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  // Audio library state
  const [uploading, setUploading] = useState(false);
  const [renamingAudio, setRenamingAudio] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {id, used_in_flows}
  const [previewFlow, setPreviewFlow] = useState(null); // flow object for preview modal
  const [simFlow, setSimFlow] = useState(null); // flow object for simulator modal

  const load = useCallback(async () => {
    try {
      const [fRes, rgRes, lRes, vRes, aRes] = await Promise.all([
        communicationsAPI.callFlows(),
        communicationsAPI.ringGroups(),
        communicationsAPI.lineSummary(),
        communicationsAPI.voiceSettings(),
        communicationsAPI.listAudio().catch(() => ({ data: [] })),
      ]);
      setFlows(fRes.data);
      setRingGroups(rgRes.data);
      setNumbers(lRes.data.numbers || []);
      setVoiceSettings(vRes.data);
      setAudioFiles(aRes.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaveFlow = async (data, numberIds) => {
    try {
      let flow;
      if (editingFlow && editingFlow !== 'new' && editingFlow.id) {
        const res = await communicationsAPI.updateCallFlow(editingFlow.id, data);
        flow = res.data;
        await communicationsAPI.assignNumbers(flow.id, numberIds);
      } else {
        const res = await communicationsAPI.createCallFlow(data);
        flow = res.data;
        if (numberIds.length > 0) {
          await communicationsAPI.assignNumbers(flow.id, numberIds);
        }
      }
      toast.success('Call flow saved');
      setEditingFlow(null);
      load();
    } catch {
      toast.error('Failed to save call flow');
    }
  };

  const handleDeleteFlow = async (id) => {
    if (!window.confirm('Delete this call flow?')) return;
    try {
      await communicationsAPI.deleteCallFlow(id);
      toast.success('Flow deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleUploadAudio = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await communicationsAPI.uploadAudio(fd);
      toast.success('Audio file uploaded');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRenameAudio = async (id) => {
    if (!renameValue.trim()) return;
    try {
      await communicationsAPI.renameAudio(id, { title: renameValue.trim() });
      toast.success('Renamed');
      setRenamingAudio(null);
      load();
    } catch { toast.error('Rename failed'); }
  };

  const handleDeleteAudio = async (id, confirm = false) => {
    try {
      const res = await communicationsAPI.deleteAudio(id, confirm);
      if (res.data.confirm_required) {
        setDeleteConfirm({ id, used_in_flows: res.data.used_in_flows, message: res.data.message });
      } else {
        toast.success('Audio file deleted');
        setDeleteConfirm(null);
        load();
      }
    } catch { toast.error('Delete failed'); }
  };

  const handleCreateRg = async () => {
    if (!newRgName.trim()) return;
    try {
      await communicationsAPI.createRingGroup({ name: newRgName.trim(), member_user_ids: [], ring_timeout: 25 });
      toast.success('Ring group created');
      setNewRgName('');
      setShowRgForm(false);
      load();
    } catch {
      toast.error('Failed to create ring group');
    }
  };

  const handleDeleteRg = async (id) => {
    if (!window.confirm('Delete this ring group?')) return;
    try {
      await communicationsAPI.deleteRingGroup(id);
      toast.success('Ring group deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleVoiceToggle = async (key, value) => {
    try {
      await communicationsAPI.updateVoiceSettings({ [key]: value });
      setVoiceSettings(prev => ({ ...prev, [key]: value }));
    } catch {
      toast.error('Failed to update setting');
    }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  if (editingFlow) {
    return <IVRFlowBuilder flow={editingFlow === 'new' ? null : editingFlow} numbers={numbers} ringGroups={ringGroups} audioFiles={audioFiles} onSave={handleSaveFlow} onCancel={() => setEditingFlow(null)} />;
  }

  return (
    <div className="space-y-6" data-testid="voice-tab">
      {/* Call Flows */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Call Flows</CardTitle>
              <CardDescription className="text-xs mt-0.5">Configure how incoming calls are routed to your team.</CardDescription>
            </div>
            <Button size="sm" className="hidden sm:inline-flex" onClick={() => setEditingFlow('new')} data-testid="new-flow-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> New Flow
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {flows.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <PhoneIncoming className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No call flows yet.</p>
              <Button size="sm" className="mt-2" onClick={() => setEditingFlow('new')}>Create Your First Flow</Button>
            </div>
          ) : (
            <div className="divide-y">
              {flows.map(f => (
                <div key={f.id} className="flex items-center justify-between gap-2 py-3" data-testid={`flow-row-${f.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{f.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span className="text-xs text-muted-foreground">{f.steps_json?.length || 0} steps</span>
                      {f.steps_json?.some(s => s.type === 'menu') && <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">IVR</Badge>}
                      {f.steps_json?.some(s => s.branches) && <Badge className="bg-teal-100 text-teal-700 text-[10px]">Branching</Badge>}
                      {f.is_default && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Default</Badge>}
                      <Badge variant="outline" className="text-[10px]">{f.assigned_number_ids?.length || 0} numbers</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setPreviewFlow(f)} data-testid={`preview-flow-${f.id}`} title="Preview flow"><Eye className="w-3.5 h-3.5 text-muted-foreground" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setSimFlow(f)} data-testid={`sim-flow-${f.id}`} title="Simulate flow"><Play className="w-3.5 h-3.5 text-blue-500" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setEditingFlow(f)} data-testid={`edit-flow-${f.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => handleDeleteFlow(f.id)} data-testid={`delete-flow-${f.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trust Hub — Business Verification (relocated to Texting Setup tab in Phase 2.1; intentionally removed here) */}

      {/* Ring Groups */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Ring Groups</CardTitle>
            <Button size="sm" variant="outline" className="hidden sm:inline-flex" onClick={() => setShowRgForm(!showRgForm)} data-testid="new-ring-group-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> New Group
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {showRgForm && (
            <div className="flex items-center gap-2 mb-4 pb-4 border-b">
              <Input value={newRgName} onChange={e => setNewRgName(e.target.value)} placeholder="Group name..." className="flex-1 sm:max-w-xs text-sm" data-testid="rg-name-input" />
              <Button size="sm" onClick={handleCreateRg} data-testid="save-rg-btn"><Check className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="ghost" onClick={() => setShowRgForm(false)}><X className="w-3.5 h-3.5" /></Button>
            </div>
          )}
          {ringGroups.length === 0 && !showRgForm ? (
            <p className="text-xs text-muted-foreground text-center py-4">No ring groups yet.</p>
          ) : (
            <div className="divide-y">
              {ringGroups.map(rg => (
                <div key={rg.id} className="flex items-center justify-between py-3" data-testid={`rg-row-${rg.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{rg.name}</p>
                    <p className="text-xs text-muted-foreground">{rg.member_user_ids?.length || 0} members · {rg.ring_timeout}s timeout</p>
                  </div>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 shrink-0" onClick={() => handleDeleteRg(rg.id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Voice settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Voice Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-3" data-testid="recording-toggle">
            <div className="min-w-0">
              <p className="text-sm font-medium">Call Recording</p>
              <p className="text-xs text-muted-foreground">Automatically record all inbound and outbound calls</p>
            </div>
            <Switch className="shrink-0" checked={voiceSettings?.call_recording_enabled || false} onCheckedChange={v => handleVoiceToggle('call_recording_enabled', v)} />
          </div>
          <div className="flex items-center justify-between gap-3" data-testid="missed-call-toggle">
            <div className="min-w-0">
              <p className="text-sm font-medium">Missed Call Auto-Text</p>
              <p className="text-xs text-muted-foreground">Send an automated SMS when a call is missed</p>
            </div>
            <Switch className="shrink-0" checked={voiceSettings?.missed_call_autotext_enabled || false} onCheckedChange={v => handleVoiceToggle('missed_call_autotext_enabled', v)} />
          </div>
        </CardContent>
      </Card>

      {/* Hold / Comfort Music */}
      <Card data-testid="hold-music-section">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Music className="w-5 h-5 text-amber-600" />
            <CardTitle className="text-base">Comfort Audio</CardTitle>
          </div>
          <CardDescription className="text-xs mt-1">
            When enabled, callers hear a short audio clip before the phone starts ringing.
            This plays once before the first ring — standard ringing follows immediately after.
            This is not custom ringback during the actual ringing phase.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-3" data-testid="hold-music-toggle">
            <div className="min-w-0">
              <p className="text-sm font-medium">Enable Comfort Audio</p>
              <p className="text-xs text-muted-foreground">Play an audio clip to callers before their call starts ringing</p>
            </div>
            <Switch
              className="shrink-0"
              checked={voiceSettings?.hold_music_enabled || false}
              onCheckedChange={v => handleVoiceToggle('hold_music_enabled', v)}
              data-testid="hold-music-switch"
            />
          </div>

          {voiceSettings?.hold_music_enabled && (
            <div className="space-y-3 pt-1" data-testid="hold-music-config">
              <div>
                <label className="text-sm font-medium block mb-1.5">Audio File</label>
                {audioFiles.length === 0 ? (
                  <p className="text-xs text-muted-foreground bg-muted/50 rounded-md p-3">
                    No audio files in your library. Upload a file in the Audio Library section below, then select it here.
                  </p>
                ) : (
                  <select
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={voiceSettings?.hold_music_audio_file_id || ''}
                    onChange={async (e) => {
                      const val = e.target.value;
                      try {
                        await communicationsAPI.updateVoiceSettings({ hold_music_audio_file_id: val || '' });
                        setVoiceSettings(prev => ({ ...prev, hold_music_audio_file_id: val || null }));
                        toast.success(val ? 'Comfort audio file selected' : 'Comfort audio file cleared');
                      } catch { toast.error('Failed to update'); }
                    }}
                    data-testid="hold-music-file-select"
                  >
                    <option value="">— Select an audio file —</option>
                    {audioFiles.map(af => (
                      <option key={af.id} value={af.id}>{af.title || af.original_filename}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Inline preview */}
              {voiceSettings?.hold_music_audio_file_id && audioFiles.find(af => af.id === voiceSettings.hold_music_audio_file_id) && (
                <div className="flex items-center gap-3 bg-muted/50 rounded-md p-3" data-testid="hold-music-preview">
                  <Play className="w-4 h-4 text-amber-600 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">
                      {audioFiles.find(af => af.id === voiceSettings.hold_music_audio_file_id)?.title || 'Selected file'}
                    </p>
                    <audio
                      controls
                      preload="none"
                      className="w-full h-8 mt-1"
                      src={communicationsAPI.audioFileUrl(voiceSettings.hold_music_audio_file_id)}
                      data-testid="hold-music-audio-player"
                    />
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-md p-3" data-testid="hold-music-info">
                <p className="text-xs text-blue-800 leading-relaxed">
                  <strong>How it works:</strong> When an inbound call arrives, this audio plays to the caller
                  before the phone begins ringing. Once the clip finishes, standard ringing starts and your
                  team's phones ring as configured in your call flow. Keep the clip short (5-15 seconds)
                  for the best caller experience.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audio Library */}
      <Card data-testid="audio-library-section">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Music className="w-5 h-5 text-muted-foreground" />
              <CardTitle className="text-base">Audio Library</CardTitle>
            </div>
            <div className="hidden sm:block">
              <label className="cursor-pointer">
                <input type="file" accept=".mp3,.wav,.ogg,.webm,audio/*" className="hidden" onChange={handleUploadAudio} disabled={uploading} data-testid="audio-upload-input" />
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border transition-colors ${uploading ? 'opacity-50 cursor-wait' : 'hover:bg-muted cursor-pointer'}`}>
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  Upload Audio
                </span>
              </label>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Upload audio files for voicemail greetings and call flow prompts. Supported: MP3, WAV, OGG, WebM (max 10MB).</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {/* Delete confirmation dialog */}
          {deleteConfirm && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2" data-testid="audio-delete-confirm">
              <p className="text-xs text-red-800 font-medium">{deleteConfirm.message}</p>
              <div className="flex flex-wrap gap-1">
                {deleteConfirm.used_in_flows.map((f, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] text-red-700">{f}</Badge>
                ))}
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                <Button size="sm" variant="destructive" onClick={() => handleDeleteAudio(deleteConfirm.id, true)} data-testid="confirm-delete-audio-btn">
                  Delete Anyway
                </Button>
              </div>
            </div>
          )}

          {audioFiles.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground" data-testid="no-audio-msg">
              <Music className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No audio files yet.</p>
              <p className="text-xs">Upload MP3, WAV, OGG, or WebM files to use in voicemail and call flows.</p>
            </div>
          ) : (
            <div className="divide-y">
              {audioFiles.map(af => (
                <div key={af.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-3" data-testid={`audio-row-${af.id}`}>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Music className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0 flex-1">
                      {renamingAudio === af.id ? (
                        <div className="flex items-center gap-1">
                          <Input
                            value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            className="h-7 text-sm"
                            autoFocus
                            onKeyDown={e => e.key === 'Enter' && handleRenameAudio(af.id)}
                            data-testid="audio-rename-input"
                          />
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleRenameAudio(af.id)}>
                            <Check className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setRenamingAudio(null)}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm font-medium truncate">{af.title}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {af.original_filename} · {af.file_size ? `${(af.file_size / 1024).toFixed(0)}KB` : ''}
                            {af.used_in_flows?.length > 0 && (
                              <span className="ml-1 text-violet-600">· Used in {af.used_in_flows.length} flow(s)</span>
                            )}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 pl-6 sm:pl-0 shrink-0">
                    <audio src={communicationsAPI.audioFileUrl(af.id)} controls preload="none" className="h-8 w-32 sm:w-40" data-testid={`audio-player-${af.id}`} />
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => { setRenamingAudio(af.id); setRenameValue(af.title); }} data-testid={`rename-audio-${af.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500 hover:text-red-700" onClick={() => handleDeleteAudio(af.id)} data-testid={`delete-audio-${af.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mobile FAB for Voice tab */}
      <MobileFABContainer data-testid="voice-fab-container">
        <MobileFABMenu open={fabOpen} onClose={() => setFabOpen(false)}>
          <MobileFABMenuItem icon={PhoneIncoming} onClick={() => { setFabOpen(false); setEditingFlow('new'); }}>
            New Call Flow
          </MobileFABMenuItem>
          <MobileFABMenuItem icon={Users} onClick={() => { setFabOpen(false); setShowRgForm(true); }}>
            New Ring Group
          </MobileFABMenuItem>
          <MobileFABMenuItem icon={Upload} onClick={() => { setFabOpen(false); document.querySelector('[data-testid="audio-upload-mobile"]')?.click(); }}>
            Upload Audio
          </MobileFABMenuItem>
        </MobileFABMenu>
        <button
          onClick={() => setFabOpen(!fabOpen)}
          data-testid="voice-mobile-fab"
          className="w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center transition-transform duration-150 active:scale-90"
        >
          <Plus className={`w-6 h-6 transition-transform ${fabOpen ? 'rotate-45' : ''}`} strokeWidth={2.5} />
        </button>
      </MobileFABContainer>
      {/* Hidden mobile upload input */}
      <input type="file" accept=".mp3,.wav,.ogg,.webm,audio/*" className="hidden" onChange={handleUploadAudio} data-testid="audio-upload-mobile" />

      {/* Flow Preview Modal */}
      {previewFlow && (
        <FlowPreviewModal
          flow={previewFlow}
          onClose={() => setPreviewFlow(null)}
          onEdit={() => { setPreviewFlow(null); setEditingFlow(previewFlow); }}
        />
      )}

      {/* Flow Simulator Modal */}
      {simFlow && (
        <FlowSimulatorModal
          flow={simFlow}
          onClose={() => setSimFlow(null)}
        />
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// SUMMARY CARD HELPER
// ═══════════════════════════════════════════════════════════

function SummaryCard({ label, value, accent, sub }) {
  const colorClass = accent === 'green' ? 'text-green-600' : accent === 'amber' ? 'text-amber-600' : 'text-foreground';
  return (
    <div className="bg-muted/50 rounded-lg p-3 text-center">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className={`text-xl font-bold mt-0.5 ${colorClass}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════

const TABS = [
  { key: 'numbers', label: 'Phone Numbers', mobileLabel: 'Numbers', icon: Phone },
  { key: 'text', label: 'Text Messaging', mobileLabel: 'SMS', icon: MessageSquare },
  { key: 'texting-setup', label: 'Texting Setup', mobileLabel: 'Setup', icon: ShieldCheck },
  { key: 'voice', label: 'Voice', mobileLabel: 'Voice', icon: Mic },
  { key: 'usage', label: 'Usage', mobileLabel: 'Usage', icon: BarChart3 },
];

export default function CommunicationsSettings() {
  const [activeTab, setActiveTab] = useState('numbers');
  const [setupState, setSetupState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    communicationsAPI.setupState().then(r => {
      setSetupState(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!setupState?.enabled) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-16" data-testid="phone-not-enabled">
          <div className="text-center">
            <Phone className="w-12 h-12 mx-auto mb-4 text-muted-foreground/40" />
            <h2 className="text-lg font-semibold mb-2">Phone System Not Enabled</h2>
            <p className="text-sm text-muted-foreground mb-6">Enable the Phone System add-on in Billing to configure communications settings.</p>
            <Button onClick={() => window.location.href = '/billing'} data-testid="go-billing-btn">
              Go to Billing <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="communications-settings">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
              <Settings className="w-5 h-5 md:w-6 md:h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold">Communications Settings</h1>
              <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">Manage phone numbers, messaging, and voice routing.</p>
            </div>
          </div>
          {setupState.tier_info && (
            <div className="flex items-center gap-2.5 bg-muted/40 sm:bg-transparent rounded-lg px-3 py-2 sm:p-0" data-testid="current-plan-info">
              <div className="text-left sm:text-right">
                <p className="text-sm font-medium">{setupState.tier_info.name}</p>
                <p className="text-xs text-muted-foreground">${(setupState.tier_info.price_cents / 100).toFixed(0)}/mo</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => window.location.href = '/phone-setup'} data-testid="change-plan-btn">
                Change Plan
              </Button>
            </div>
          )}
        </div>

        {/* Setup banner */}
        {!setupState.setup_complete && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4" data-testid="setup-incomplete-banner">
            <div className="flex items-start sm:items-center gap-2">
              <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 shrink-0 mt-0.5 sm:mt-0" />
              <p className="text-xs sm:text-sm text-amber-800">Phone setup is not complete. Complete setup to get the most out of your phone system.</p>
            </div>
            <Button size="sm" variant="outline" className="self-end sm:self-auto shrink-0" onClick={() => window.location.href = '/phone-setup'} data-testid="complete-setup-btn">
              Complete Setup
            </Button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 sm:gap-1.5 bg-muted/50 rounded-lg p-1" data-testid="comms-tabs">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex-1 flex items-center justify-center gap-1 sm:gap-1.5 px-1.5 sm:px-4 py-2 sm:py-2.5 rounded-md text-[11px] sm:text-sm transition-colors ${activeTab === tab.key ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid={`tab-${tab.key}`}>
                <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                <span className="truncate sm:hidden">{tab.mobileLabel}</span>
                <span className="truncate hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        {activeTab === 'numbers' && <PhoneNumbersTab />}
        {activeTab === 'text' && <TextMessagingTab />}
        {activeTab === 'texting-setup' && <TextingSetupTab />}
        {activeTab === 'voice' && <VoiceTab />}
        {activeTab === 'usage' && <UsageDashboardTab />}
      </div>
    </Layout>
  );
}
